import React, { useState, useEffect } from 'react';
import ApiService from '../../../services/api';
import { getToken } from '../../../utils/auth';
import { useNotification } from '../../../context/NotificationContext';

interface TagDefinition {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string | null;
  versions: TagDefinitionSpecVersion[];
  relatedTags: TagDefinition[];
  tags: Tag[];
  requiredOnIngestion: boolean;
}

interface TagDefinitionSpecVersion {
  id: number;
  spec: any;
  createdAt: string;
  updatedAt: string | null;
}

interface Tag {
  id: number;
  name: string;
  value: any;
}

interface PaginationMeta {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

// Helper component to display spec JSON in a structured way
const SpecDisplay = ({ spec }: { spec: any }) => {
  if (!spec) return <div className="text-gray-500">No spec data</div>;
  
  let parsedSpec = spec;
  
  // If spec is a string that looks like JSON, try to parse it
  if (typeof spec === 'string' && (spec.startsWith('{') || spec.startsWith('['))) {
    try {
      parsedSpec = JSON.parse(spec);
    } catch (e) {
      // If parsing fails, just use the original string
      parsedSpec = spec;
    }
  }
  
  // If it's still a string after attempted parsing, just display it
  if (typeof parsedSpec === 'string') {
    return <div className="whitespace-pre-wrap">{parsedSpec}</div>;
  }
  
  // If it's an object with specific fields we know how to display nicely
  if (parsedSpec && typeof parsedSpec === 'object') {
    return (
      <div className="space-y-3">
        {parsedSpec.description && (
          <div className="mb-2">
            <span className="font-medium text-gray-700">Description: </span>
            <span>{parsedSpec.description}</span>
          </div>
        )}
        
        {parsedSpec.value_format && (
          <div className="mb-2">
            <span className="font-medium text-gray-700">Value Format: </span>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
              {parsedSpec.value_format}
            </span>
          </div>
        )}
        
        {parsedSpec.criteria && Array.isArray(parsedSpec.criteria) && parsedSpec.criteria.length > 0 && (
          <div>
            <span className="font-medium text-gray-700 block mb-1">Criteria:</span>
            <ul className="list-disc pl-5 space-y-1">
              {parsedSpec.criteria.map((criterion: string, index: number) => (
                <li key={index} className="text-sm">{criterion}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Display any other fields as key-value pairs */}
        {Object.entries(parsedSpec)
          .filter(([key]) => !['description', 'value_format', 'criteria'].includes(key))
          .map(([key, value]) => (
            <div key={key} className="mb-1">
              <span className="font-medium text-gray-700">{key}: </span>
              <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
            </div>
          ))}
      </div>
    );
  }
  
  // Fallback to JSON.stringify with formatting for any other object
  return (
    <pre className="bg-gray-50 p-3 rounded text-sm overflow-auto max-h-60">
      {JSON.stringify(parsedSpec, null, 2)}
    </pre>
  );
};

const TagDefinitions = () => {
  const [definitions, setDefinitions] = useState<TagDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [nameFilter, setNameFilter] = useState('');
  const [debouncedNameFilter, setDebouncedNameFilter] = useState('');
  const [requiredOnIngestionFilter, setRequiredOnIngestionFilter] = useState(false);
  const [newDefinition, setNewDefinition] = useState({
    name: '',
    spec: '',
    relatedTags: [] as string[],
    requiredOnIngestion: false,
  });
  const [editingDefinition, setEditingDefinition] = useState<TagDefinition | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    spec: '',
    relatedTags: [] as string[],
    requiredOnIngestion: false,
  });
  const [originalSpec, setOriginalSpec] = useState('');
  const [specModified, setSpecModified] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<Record<number, number>>({});
  const [pagination, setPagination] = useState<PaginationMeta>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const { showNotification } = useNotification();

  // Debounce the name filter to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNameFilter(nameFilter);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [nameFilter]);

  // Fetch definitions when page or filter changes
  useEffect(() => {
    fetchDefinitions(pagination.currentPage, debouncedNameFilter, requiredOnIngestionFilter);
  }, [pagination.currentPage, debouncedNameFilter, requiredOnIngestionFilter]);

  // Initialize selected versions when definitions change
  useEffect(() => {
    const newSelectedVersions: Record<number, number> = {};
    
    definitions.forEach(definition => {
      if (definition.versions && definition.versions.length > 0) {
        // Sort versions by ID in descending order (assuming higher IDs are newer)
        const sortedVersions = [...definition.versions].sort((a, b) => b.id - a.id);
        // Select the highest ID version by default
        newSelectedVersions[definition.id] = sortedVersions[0].id;
      }
    });
    
    setSelectedVersions(newSelectedVersions);
  }, [definitions]);

  const fetchDefinitions = async (page: number = 1, name: string = '', requiredOnly: boolean = false) => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        setLoading(false);
        return;
      }

      const api = new ApiService(token);
      const response = await api.getTagDefinitions(page, pagination.itemsPerPage, name, requiredOnly);
      console.log('Tag Definitions API response:', response);
      
      if (response) {
        // Handle response with pagination metadata
        if (response && typeof response === 'object' && 'items' in response && Array.isArray(response.items)) {
          setDefinitions(response.items);
          if ('meta' in response && response.meta) {
            const meta = response.meta as {
              currentPage?: number;
              totalPages?: number;
              totalItems?: number;
              itemsPerPage?: number;
            };
            setPagination({
              currentPage: meta.currentPage || page,
              totalPages: meta.totalPages || 1,
              totalItems: meta.totalItems || response.items.length,
              itemsPerPage: meta.itemsPerPage || pagination.itemsPerPage
            });
          }
        } 
        // Handle response with just an array
        else if (Array.isArray(response)) {
          setDefinitions(response);
        } 
        else {
          console.warn('Unexpected API response format:', response);
          setDefinitions([]);
        }
      } else {
        console.warn('No data in API response:', response);
        setDefinitions([]);
      }
    } catch (error) {
      console.error('Error fetching tag definitions:', error);
      showNotification('Failed to fetch tag definitions. Please try again.', 'error');
      setDefinitions([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination({
        ...pagination,
        currentPage: newPage
      });
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNameFilter(e.target.value);
    // Reset to first page when filter changes
    setPagination({
      ...pagination,
      currentPage: 1
    });
  };

  const handleClearFilter = () => {
    setNameFilter('');
    setRequiredOnIngestionFilter(false);
    // Reset to first page when filter is cleared
    setPagination({
      ...pagination,
      currentPage: 1
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let specData = {};
      if (newDefinition.spec) {
        try {
          specData = JSON.parse(newDefinition.spec);
        } catch (error) {
          showNotification('Invalid JSON in spec field. Please check your input.', 'error');
          return;
        }
      }

      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const createdDefinition = await api.createTagDefinition({
        name: newDefinition.name,
        spec: specData,
        relatedTags: newDefinition.relatedTags,
        requiredOnIngestion: newDefinition.requiredOnIngestion,
      });

      showNotification('Tag definition created successfully!', 'success');
      setNewDefinition({ name: '', spec: '', relatedTags: [], requiredOnIngestion: false });
      fetchDefinitions(1, debouncedNameFilter, requiredOnIngestionFilter); // Refresh and go back to first page
    } catch (error) {
      console.error('Error creating tag definition:', error);
      showNotification('Failed to create tag definition. Please try again.', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      await api.deleteTagDefinition(id);

      showNotification('Tag definition deleted successfully!', 'success');
      
      // If we're on a page with only one item and it's not the first page,
      // go to the previous page after deletion
      if (definitions.length === 1 && pagination.currentPage > 1) {
        setPagination({
          ...pagination,
          currentPage: pagination.currentPage - 1
        });
      } else {
        fetchDefinitions(pagination.currentPage, debouncedNameFilter, requiredOnIngestionFilter);
      }
    } catch (error) {
      console.error('Error deleting tag definition:', error);
      showNotification('Failed to delete tag definition. Please try again.', 'error');
    }
  };

  const handleEdit = (definition: TagDefinition) => {
    setEditingDefinition(definition);
    
    // Get the selected spec version, or the latest if none is selected
    const selectedVersionId = selectedVersions[definition.id];
    
    // Sort versions by ID in descending order
    const sortedVersions = definition.versions && definition.versions.length > 0
      ? [...definition.versions].sort((a, b) => b.id - a.id)
      : [];
      
    const selectedVersion = sortedVersions.length > 0
      ? sortedVersions.find(v => v.id === selectedVersionId) || sortedVersions[0]
      : null;
    
    const latestSpec = selectedVersion
      ? selectedVersion.spec
      : {};
    
    setEditFormData({
      name: definition.name,
      spec: typeof latestSpec === 'string' ? latestSpec : JSON.stringify(latestSpec, null, 2),
      relatedTags: definition.relatedTags ? definition.relatedTags.map(tag => tag.name) : [],
      requiredOnIngestion: definition.requiredOnIngestion,
    });
    
    setOriginalSpec(typeof latestSpec === 'string' ? latestSpec : JSON.stringify(latestSpec, null, 2));
    setSpecModified(false);
    
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDefinition) return;

    try {
      let specData = {};
      if (editFormData.spec) {
        try {
          specData = JSON.parse(editFormData.spec);
        } catch (error) {
          showNotification('Invalid JSON in spec field. Please check your input.', 'error');
          return;
        }
      }

      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      
      // First update the tag definition (without spec)
      await api.updateTagDefinition(editingDefinition.id, {
        name: editFormData.name,
        relatedTags: editFormData.relatedTags,
        requiredOnIngestion: editFormData.requiredOnIngestion,
      });
      
      // Check if spec has changed before creating a new version
      if (editFormData.spec && Object.keys(specData).length > 0) {
        // Only create a new version if the spec has been modified
        if (specModified) {
          await api.createTagDefinitionSpecVersion(editingDefinition.id, specData);
          showNotification('Tag definition updated with new spec version!', 'success');
        } else {
          showNotification('Tag definition updated! (No changes to spec detected)', 'success');
        }
      } else {
        showNotification('Tag definition updated!', 'success');
      }
      
      setShowEditModal(false);
      fetchDefinitions(pagination.currentPage, debouncedNameFilter, requiredOnIngestionFilter);
    } catch (error) {
      console.error('Error updating tag definition:', error);
      showNotification('Failed to update tag definition. Please try again.', 'error');
    }
  };

  const handleSpecChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newSpec = e.target.value;
    setEditFormData({ ...editFormData, spec: newSpec });
    
    // Check if the spec has been modified
    const normalizeJson = (str: string) => {
      try {
        return JSON.stringify(JSON.parse(str), null, 0);
      } catch (e) {
        return str;
      }
    };
    
    const isModified = normalizeJson(newSpec) !== normalizeJson(originalSpec);
    setSpecModified(isModified);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingDefinition(null);
    setSpecModified(false);
  };

  const handleVersionChange = (definitionId: number, versionId: number) => {
    setSelectedVersions(prev => ({
      ...prev,
      [definitionId]: versionId
    }));
  };

  // Pagination UI component
  const Pagination = () => {
    if (pagination.totalPages <= 1) return null;
    
    return (
      <div className="flex justify-center mt-4 space-x-2">
        <button
          onClick={() => handlePageChange(1)}
          disabled={pagination.currentPage === 1}
          className={`px-3 py-1 rounded ${
            pagination.currentPage === 1
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          First
        </button>
        <button
          onClick={() => handlePageChange(pagination.currentPage - 1)}
          disabled={pagination.currentPage === 1}
          className={`px-3 py-1 rounded ${
            pagination.currentPage === 1
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Previous
        </button>
        <div className="px-3 py-1 bg-blue-600 text-white rounded">
          {pagination.currentPage} of {pagination.totalPages}
        </div>
        <button
          onClick={() => handlePageChange(pagination.currentPage + 1)}
          disabled={pagination.currentPage === pagination.totalPages}
          className={`px-3 py-1 rounded ${
            pagination.currentPage === pagination.totalPages
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Next
        </button>
        <button
          onClick={() => handlePageChange(pagination.totalPages)}
          disabled={pagination.currentPage === pagination.totalPages}
          className={`px-3 py-1 rounded ${
            pagination.currentPage === pagination.totalPages
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Last
        </button>
      </div>
    );
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Manage Tag Definitions</h2>

      {/* Filter section */}
      <div className="mb-4 bg-white p-4 rounded shadow">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="flex-grow">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Name
            </label>
            <div className="relative">
              <input
                type="text"
                value={nameFilter}
                onChange={handleFilterChange}
                className="w-full p-2 pr-10 border border-gray-300 rounded"
                placeholder="Search tag definitions..."
              />
              {nameFilter && (
                <button
                  onClick={handleClearFilter}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <div className="md:self-end flex items-center mt-2 md:mt-0">
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={requiredOnIngestionFilter}
                onChange={(e) => setRequiredOnIngestionFilter(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span>Required on Ingestion Only</span>
            </label>
            {requiredOnIngestionFilter && (
              <button
                onClick={() => setRequiredOnIngestionFilter(false)}
                className="ml-2 text-gray-400 hover:text-gray-600"
                title="Clear required filter"
              >
                ✕
              </button>
            )}
          </div>
          <div className="md:self-end">
            {(debouncedNameFilter || requiredOnIngestionFilter) && (
              <div className="text-sm text-gray-500 mt-1 md:mt-0">
                Filtering by: 
                {debouncedNameFilter && <span className="font-medium"> "{debouncedNameFilter}"</span>}
                {debouncedNameFilter && requiredOnIngestionFilter && <span> and </span>}
                {requiredOnIngestionFilter && <span className="font-medium">Required on Ingestion</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mb-6 bg-white p-4 rounded shadow">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Definition Name
            </label>
            <input
              type="text"
              value={newDefinition.name}
              onChange={(e) =>
                setNewDefinition({ ...newDefinition, name: e.target.value })
              }
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="Enter definition name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Spec (JSON)
            </label>
            <textarea
              value={newDefinition.spec}
              onChange={(e) =>
                setNewDefinition({ ...newDefinition, spec: e.target.value })
              }
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="Enter JSON spec"
              rows={4}
            />
          </div>
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-1">
              <input
                type="checkbox"
                checked={newDefinition.requiredOnIngestion}
                onChange={(e) =>
                  setNewDefinition({ ...newDefinition, requiredOnIngestion: e.target.checked })
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span>Required On Ingestion</span>
            </label>
            <p className="text-xs text-gray-500 ml-6">
              When enabled, this tag must be provided when ingesting data
            </p>
          </div>
          <div className="md:col-span-2">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
            >
              Create Definition
            </button>
          </div>
        </div>
      </form>

      {loading ? (
        <div className="text-center py-4">Loading tag definitions...</div>
      ) : definitions && definitions.length > 0 ? (
        <>
          <div className="space-y-4">
            {definitions.map((definition) => (
              <div
                key={definition.id}
                className="bg-white rounded shadow overflow-hidden"
              >
                <div className="p-4 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-medium">{definition.name}</h3>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {definition.requiredOnIngestion && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Required on Ingestion
                          </span>
                        )}
                        {definition.versions && definition.versions.length > 0 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {definition.versions.length} Version{definition.versions.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <button
                        onClick={() => handleEdit(definition)}
                        className="text-blue-600 hover:text-blue-900 mr-2"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(definition.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-gray-500">
                    Created: {definition.createdAt ? new Date(definition.createdAt).toLocaleString() : '-'}
                  </div>
                </div>

                {definition.versions && definition.versions.length > 0 && (
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-medium text-gray-700">
                        Spec Versions
                      </h4>
                      <div className="flex items-center">
                        <label htmlFor={`version-select-${definition.id}`} className="text-sm text-gray-600 mr-2">
                          Version:
                        </label>
                        <select
                          id={`version-select-${definition.id}`}
                          value={selectedVersions[definition.id] || ''}
                          onChange={(e) => handleVersionChange(definition.id, Number(e.target.value))}
                          className="text-sm border border-gray-300 rounded p-1"
                        >
                          {[...definition.versions]
                            .sort((a, b) => b.id - a.id) // Sort by ID in descending order
                            .map((version) => (
                              <option key={version.id} value={version.id}>
                                Version #{version.id}
                              </option>
                            ))
                          }
                        </select>
                      </div>
                    </div>
                    
                    {/* Display only the selected version */}
                    {definition.versions.map((version) => (
                      version.id === selectedVersions[definition.id] && (
                        <div
                          key={version.id}
                          className="text-sm bg-gray-50 p-3 rounded"
                        >
                          <SpecDisplay spec={version.spec} />
                          <div className="mt-2 text-xs text-gray-500">
                            Version #{version.id}
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                )}

                {definition.tags && definition.tags.length > 0 && (
                  <div className="p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Associated Tags
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {definition.tags.map((tag) => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination />
          <div className="mt-2 text-sm text-gray-500 text-center">
            Showing {definitions.length} of {pagination.totalItems} tag definitions
            {debouncedNameFilter && <span> matching "{debouncedNameFilter}"</span>}
            {requiredOnIngestionFilter && <span> that are required on ingestion</span>}
            <div className="mt-1">
              {definitions.filter(d => d.requiredOnIngestion).length} of {definitions.length} shown are required on ingestion
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-4 bg-white rounded shadow">
          {debouncedNameFilter 
            ? `No tag definitions found matching "${debouncedNameFilter}". Try a different filter or create a new definition.`
            : 'No tag definitions found. Create one above.'}
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg max-w-2xl w-full">
            <h2 className="text-xl font-semibold mb-4">Edit Tag Definition</h2>
            {editingDefinition && editingDefinition.versions && editingDefinition.versions.length > 0 && (
              <div className="mb-4 text-sm text-gray-600">
                <p>
                  Editing Version #{selectedVersions[editingDefinition.id] || 'Unknown'}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Note: A new version will only be created if the spec content changes.
                </p>
              </div>
            )}
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Definition Name
                </label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, name: e.target.value })
                  }
                  className="w-full p-2 border border-gray-300 rounded"
                  placeholder="Enter definition name"
                  required
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Spec (JSON)
                  </label>
                  {specModified ? (
                    <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                      Modified - Will create new version
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                      Unchanged - No new version needed
                    </span>
                  )}
                </div>
                <textarea
                  value={editFormData.spec}
                  onChange={handleSpecChange}
                  className="w-full p-2 border border-gray-300 rounded"
                  placeholder="Enter JSON spec"
                  rows={4}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Related Tags
                </label>
                <input
                  type="text"
                  value={editFormData.relatedTags.join(', ')}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, relatedTags: e.target.value.split(',').map(t => t.trim()) })
                  }
                  className="w-full p-2 border border-gray-300 rounded"
                  placeholder="Enter related tag names separated by commas"
                />
              </div>
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-1">
                  <input
                    type="checkbox"
                    checked={editFormData.requiredOnIngestion}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, requiredOnIngestion: e.target.checked })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span>Required On Ingestion</span>
                </label>
                <p className="text-xs text-gray-500 ml-6">
                  When enabled, this tag must be provided when ingesting data
                </p>
              </div>
              <div>
                <button
                  type="submit"
                  className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
                >
                  Update Definition
                </button>
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="bg-gray-200 text-gray-700 py-2 px-4 rounded ml-2 hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagDefinitions; 